import { useState } from "react";
import api from "@/lib/api";
import { relDate, isOverdue } from "@/lib/crm";
import { EditActivityModal } from "@/components/crm/EditActivityModal";
import { EditAppointmentModal } from "@/components/crm/EditAppointmentModal";
import { Pencil, Check, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Colonna "Prossima azione": attività attiva più vicina (derivata), cliccabile per modificarla
export default function NextActionCell({ lead, canEdit, onChanged, testid = "next-action-edit" }) {
  const [task, setTask] = useState(null);
  const [appt, setAppt] = useState(null);
  if (!lead.next_contact_at) return <span className="text-xs text-muted-foreground">—</span>;
  const ref = lead.next_ref;
  const overdue = isOverdue(lead.next_contact_at);
  const label = (
    <>
      {overdue && "⚠️ "}
      {ref?.type_label && <span className="font-semibold">{ref.type_label}: </span>}
      {(ref?.kind === "appointment" ? ref.title.replace(/^Appuntamento · /, "") : lead.next_action) || "Contatto"} · {relDate(lead.next_contact_at)}
    </>
  );
  const cls = cn("text-xs text-left", overdue ? "text-amber-600 dark:text-amber-400 font-medium" : "text-muted-foreground");
  if (!canEdit || !ref) return <span className={cls}>{label}</span>;

  const open = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.get(`/leads/${lead.id}`);
      if (ref.kind === "task") {
        const t = (data.tasks || []).find((x) => x.id === ref.id);
        t ? setTask(t) : toast.error("Attività non più disponibile");
      } else {
        const a = (data.appointments || []).find((x) => x.id === ref.id);
        a ? setAppt(a) : toast.error("Appuntamento non più disponibile");
      }
    } catch { toast.error("Impossibile aprire l'attività"); }
  };
  const done = () => { setTask(null); setAppt(null); onChanged && onChanged(); };
  const actions = <ActivityActions task={task} appt={appt} onDone={done} />;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <button onClick={open} data-testid={`${testid}-${lead.id}`} title="Modifica attività"
        className={cn(cls, "hover:underline inline-flex items-center gap-1 group")}>
        <span>{label}</span><Pencil className="w-3 h-3 opacity-0 group-hover:opacity-100" />
      </button>
      <EditActivityModal task={task} open={!!task} onClose={() => setTask(null)} onDone={onChanged} extraActions={actions} />
      <EditAppointmentModal appt={appt} open={!!appt} onClose={() => setAppt(null)} onDone={onChanged} extraActions={actions} />
    </span>
  );
}

// Pulsanti "Completa" / "Elimina" (con conferma) per un'attività o un appuntamento; la cronologia resta
export function ActivityActions({ task, appt, onDone }) {
  const complete = async () => {
    try {
      if (task) await api.post(`/tasks/${task.id}/complete`);
      else await api.put(`/appointments/${appt.id}/status`, { status: "EFFETTUATO" });
      toast.success(task ? "Attività completata" : "Appuntamento segnato come effettuato");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  const remove = async () => {
    const what = task ? "questa attività" : "questo appuntamento";
    if (!window.confirm(`Sei sicuro di voler eliminare ${what}? La cronologia del lead resterà invariata.`)) return;
    try {
      await api.delete(task ? `/tasks/${task.id}` : `/appointments/${appt.id}`);
      toast.success("Eliminato");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };
  if (!task && !appt) return null;
  return (
    <>
      <Button size="sm" variant="outline" className="gap-1 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" onClick={complete} data-testid="next-action-complete"><Check className="w-3.5 h-3.5" />Completa</Button>
      <Button size="sm" variant="outline" className="gap-1 text-red-600 border-red-500/40" onClick={remove} data-testid="next-action-delete"><Trash2 className="w-3.5 h-3.5" />Elimina</Button>
    </>
  );
}
